import React, { useEffect } from "react";
import clsx from "clsx";

import {
  ComposedChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  Cell,
  Line,
} from "recharts";

import missing_image from "./../../i/missing_image.svg";

import {
  MetricTable,
  selectFormat,
  CustomizedYAxisTick,
  CustomizedYAxisTickPercentage,
  StandardTooltip,
  Error,
  truncate,
  getAllKeys,
  MAIN_COLORS,
} from "./../Utils/Utils.js";

const metricBar = (cell) => {
  const props = { ...cell };
  return <MetricTable {...props} graph={props.trends} graphType="bar" />;
};

const DailyLineCharts = (props) => {
  const { chart: data, phases } = props.data;

  const getExportData = () => ({
    data: data,
    opts: {
      type: "other",
    },
  });

  useEffect(() => {
    props.childRef({ getExportData });
  });

  if (Object.keys(data).length <= 1) {
    return <Error minHeight={360} />;
  }

  // For Phase
  const getPhaseWidths = () => {
    const activePhases = phases.filter((phase) => phase.highlight === true);
    if (activePhases.length && data.length) {
      activePhases.unshift({
        start: data[0].date,
        end: activePhases[0].start,
      });
    }

    return activePhases.map((el, i) => {
      const endIndex = data.findIndex((d) => d.date === el.end);
      const duration =
        (endIndex < 0 ? data.length - 1 : endIndex) -
        data.findIndex((d) => d.date === el.start) +
        1 +
        (i === 0 ? -1 : 0); // need for the first empty phase

      return {
        name: el.name,
        width: (duration < 0 ? 0 : (duration / data.length) * 100) + "%",
      };
    });
  };

  const phaseWidths = getPhaseWidths(phases);

  const renderBars = (data) => {
    let bars = [];
    let all_bars = [];

    const sortedKeys = getSortedKeys(data);

    for (let i = 0; i <= data.length - 1; i++) {
      sortedKeys.forEach((source) => all_bars.push(source));
    }

    const isFixedBarWidth = data.length <= 7;

    [...new Set(all_bars)].forEach((bar, i) => {
      // exclude view_rate or engagement_rate
      if (bar !== "view_rate" || bar !== "engagement_rate")
        bars.push(
          <Bar
            key={i}
            dataKey={bar}
            stackId="stack"
            barSize={isFixedBarWidth ? 35 : null}
            fill={MAIN_COLORS[i]}
            yAxisId="left"
          >
            {data.map(
              (entry, index) =>
                entry && (
                  <Cell
                    key={`cell-${bar}-${index}`}
                    fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                  />
                )
            )}
          </Bar>
        );
    });

    return bars;
  };

  const legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="mt-2 legend mt-md-0">
        <span
          className="legend__icon"
          style={{ background: MAIN_COLORS[i] }}
        ></span>
        <span className="legend__text" style={{ color: MAIN_COLORS[i] }}>
          {entry.replace(/_/g, " ")}
        </span>
      </span>
    ));

  const renderLegends = (data) => {
    let legends = [];
    let all_legends = [];

    const sortedKeys = getSortedKeys(data);

    sortedKeys.forEach((source) => all_legends.push(source));

    legends = [...new Set(all_legends)];
    return legendElement(legends);
  };

  const getSortedKeys = (_data) => {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);

    const keys = getAllKeys(data, ["date"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  };

  const renderPhaseTitles = () => (
    <div className="d-flex phase-titles">
      {phaseWidths.map((el, i) => (
        <div
          key={i}
          style={{ width: el.width }}
          className={clsx("phase-titles__item", { "no-boder": i === 0 })}
        >
          <span>{el.name}</span>
        </div>
      ))}
    </div>
  );

  const getPhaseStyles = (el, i) => ({
    width: el.width,
    backgroundColor: i % 2 === 1 ? "#FEF0EE" : "",
  });

  const formatTooltipValue = (key, data) => {
    let formatted_value = "";
    switch (key) {
      case "view_rate":
      case "engagement_rate":
        formatted_value = selectFormat("percentage", data[key]);
        break;
      default:
        formatted_value = selectFormat("number", data[key]);
    }

    return formatted_value;
  };

  const renderChart = (data) => (
    <>
      {renderLegends(data)}

      <div className="mt-3 composed-chart position-relative">
        {/*  Phase Background */}
        <div className="composed-chart__phase">
          {phaseWidths.map((el, i) => (
            <div
              key={i}
              className="composed-chart__phase--item"
              style={getPhaseStyles(el, i)}
            />
          ))}
        </div>

        <ResponsiveContainer height={350} width="100%">
          <ComposedChart
            data={data}
            margin={{ top: 12, right: 0, bottom: 0, left: -38 }}
            stackOffset="sign"
          >
            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />

            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={({ data }) => {
                    const payload = data(props);

                    if (payload !== null) {
                      const tooltip_data = payload[0].payload;
                      let total = "";

                      if (tooltip_data.total) {
                        total = tooltip_data.total;
                      } else {
                        total = payload.reduce(
                          (acc, cur) => acc + (cur.value || 0),
                          0
                        );
                      }

                      const topContent = tooltip_data.topContent;
                      let IMAGE = {
                        backgroundImage: `url(${missing_image})`,
                      };

                      if (topContent) {
                        if (topContent.thumbnail)
                          IMAGE = {
                            backgroundImage: `url(${topContent.thumbnail})`,
                          };

                        topContent.value = topContent.views;
                        topContent.trends.forEach((chartData) => {
                          chartData.value = chartData.views;
                          chartData.label = chartData.date;

                          chartData.selected =
                            tooltip_data.name &&
                            tooltip_data.name === chartData.date;
                        });
                      }

                      return (
                        <div className="standard-tooltip">
                          {tooltip_data.date && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:
                              </span>
                              <span className="standard-tooltip__value-header">
                                {tooltip_data.date}
                              </span>
                            </header>
                          )}
                          <main className="standard-tooltip__main">
                            {Object.keys(tooltip_data)
                              .filter((key) => key !== "date")
                              .map((key, index) => (
                                <div
                                  className="standard-tooltip__item"
                                  key={key}
                                >
                                  <span
                                    className="standard-tooltip__icon"
                                    style={{ background: MAIN_COLORS[index] }}
                                  ></span>
                                  <span
                                    className="standard-tooltip__key text-capitalize"
                                    style={{ color: MAIN_COLORS[index] }}
                                  >
                                    {key.replace(/_/g, " ")}
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {formatTooltipValue(key, tooltip_data)}
                                  </span>
                                </div>
                              ))}

                            <div className="standard-tooltip__dilimiter1"></div>

                            <div className="standard-tooltip__item">
                              <span className="standard-tooltip__key text-capitalize total">
                                Total{" "}
                              </span>
                              <span className="standard-tooltip__value">
                                {selectFormat("number", total)}
                              </span>
                            </div>

                            {topContent && (
                              <div className="mt-3 standard-tooltip__dilimiter2" />
                            )}
                            {topContent && (
                              <div className="my-2 standard-tooltip__section-header">
                                Top content on this day.
                              </div>
                            )}

                            {topContent ? (
                              <div className="standard-tooltip__details--posts">
                                <article className="content-table">
                                  <figure className="content-table__figure">
                                    <div
                                      className="content-table__img"
                                      style={IMAGE}
                                    />
                                  </figure>

                                  <section className="content-table__detail">
                                    <span className="standard-tooltip__value">
                                      {metricBar(topContent)}
                                    </span>
                                  </section>
                                </article>

                                <div className="standard-tooltip__text">
                                  {truncate(topContent.title, 200)}
                                </div>
                              </div>
                            ) : null}
                          </main>
                        </div>
                      );
                    }
                  }}
                />
              }
            />

            {renderBars(data)}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="view_rate"
              stroke={MAIN_COLORS["view_rate"]}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="engagement_rate"
              stroke={MAIN_COLORS["engagement_rate"]}
            />

            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              scale="auto"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={(d) => selectFormat("number", d)}
              tick={<CustomizedYAxisTick />}
              yAxisId="left"
              orientation="left"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={(d) => selectFormat("percentage", d)}
              tick={<CustomizedYAxisTickPercentage />}
              yAxisId="right"
              orientation="right"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {renderPhaseTitles()}
    </>
  );

  return renderChart(data);
};

export default DailyLineCharts;
